import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { API_URL } from '../../config';

function Dashboard() {
  const [stats, setStats] = useState({
    articleCount: 0,
    feedbackCount: 0,
    recentArticles: [],
    recentFeedback: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState('');
  const [latestSummary, setLatestSummary] = useState(null);
  const [topArticles, setTopArticles] = useState([]);
  const [topViewed, setTopViewed] = useState([]);

  useEffect(() => {
    async function fetchDashboardData() {
      try {
        const token = localStorage.getItem('adminToken');
        
        // Get recent articles
        const articlesRes = await axios.get(`${API_URL}/articles?limit=5&sort=-createdAt`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Get recent feedback
        const feedbackRes = await axios.get(`${API_URL}/feedback?limit=5&sort=-createdAt`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Get total counts
        const articlesCountRes = await axios.get(`${API_URL}/articles/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const feedbackCountRes = await axios.get(`${API_URL}/feedback/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setStats({
          articleCount: articlesCountRes.data.total || articlesRes.data.totalDocs || articlesRes.data.total || 0,
          feedbackCount: feedbackCountRes.data.total || feedbackRes.data.totalDocs || feedbackRes.data.total || 0,
          recentArticles: articlesRes.data.data || [],
          recentFeedback: feedbackRes.data.data || []
        });
        
        setLoading(false);
      } catch (err) {
        console.error('Dashboard data error:', err);
        setError('Failed to load dashboard data');
        setLoading(false);
      }
    }

    fetchDashboardData();
  }, []);

  // Fetch analytics (latest newsletter summary + top articles)
  useEffect(() => {
    async function fetchAnalytics() {
      setAnalyticsLoading(true);
      setAnalyticsError('');
      try {
        const token = localStorage.getItem('adminToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const latestNewsletterRes = await axios.get(`${API_URL}/newsletters/latest`, { headers });
        const latest = latestNewsletterRes?.data?.data;
        if (latest && latest._id) {
          const summaryRes = await axios.get(`${API_URL}/analytics/newsletters/${latest._id}/summary`, { headers });
          setLatestSummary(summaryRes?.data?.data || null);
        } else {
          setLatestSummary(null);
        }

        const topRes = await axios.get(`${API_URL}/analytics/top-articles`, { headers });
        setTopArticles(topRes?.data?.data || []);
        const topViewedRes = await axios.get(`${API_URL}/analytics/top-viewed-articles`, { headers });
        setTopViewed(topViewedRes?.data?.data || []);
      } catch (err) {
        console.error('Analytics fetch error:', err);
        setAnalyticsError(err.response?.data?.error || err.message || 'Failed to load analytics');
      }
      setAnalyticsLoading(false);
    }

    fetchAnalytics();
  }, []);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-10">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-2 text-gray-600">Loading dashboard...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex">
            <div>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Admin Dashboard</h1>

      {/* Analytics Summary (Latest Newsletter) */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-3">Analytics (Latest Newsletter)</h2>
        {analyticsLoading ? (
          <div className="text-gray-500">Loading analytics...</div>
        ) : analyticsError ? (
          <div className="text-red-600">{analyticsError}</div>
        ) : latestSummary ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-gray-500 text-sm uppercase">Preview Views</div>
              <div className="text-2xl font-bold">{latestSummary.previewViews || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-gray-500 text-sm uppercase">Total Opens</div>
              <div className="text-2xl font-bold">{latestSummary.totalOpens || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-gray-500 text-sm uppercase">Unique Opens</div>
              <div className="text-2xl font-bold">{latestSummary.uniqueOpens || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-gray-500 text-sm uppercase">Total Clicks</div>
              <div className="text-2xl font-bold">{latestSummary.totalClicks || 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-gray-500 text-sm uppercase">Unique Clicks</div>
              <div className="text-2xl font-bold">{latestSummary.uniqueClicks || 0}</div>
            </div>
          </div>
        ) : (
          <div className="text-gray-500">No latest newsletter found.</div>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Articles</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-gray-900">{stats.articleCount}</div>
                </dd>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6">
            <div className="text-sm">
              <a href="/admin/articles" className="font-medium text-blue-600 hover:text-blue-500">View all articles</a>
            </div>
          </div>
        </div>
        
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
                <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Feedback</dt>
                <dd className="flex items-baseline">
                  <div className="text-2xl font-semibold text-gray-900">{stats.feedbackCount}</div>
                </dd>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6">
            <div className="text-sm">
              <a href="/admin/feedback" className="font-medium text-blue-600 hover:text-blue-500">View all feedback</a>
            </div>
          </div>
        </div>
      </div>
      
      {/* Top Articles (Clicks + Reactions) */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-3">Top Articles (Recent)</h2>
        {analyticsLoading ? (
          <div className="text-gray-500">Loading top articles...</div>
        ) : topArticles.length === 0 ? (
          <div className="text-gray-500">No data available.</div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-500 uppercase tracking-wider">Article ID</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500 uppercase tracking-wider">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {topArticles.map((row) => (
                  <tr key={row.articleId} className="hover:bg-gray-50">
                    <td className="py-3 px-4 font-mono text-sm">{row.articleId}</td>
                    <td className="py-3 px-4">{row.title}</td>
                    <td className="py-3 px-4">{row.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top Viewed Articles */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold mb-3">Top Viewed Articles</h2>
        {analyticsLoading ? (
          <div className="text-gray-500">Loading top viewed...</div>
        ) : topViewed.length === 0 ? (
          <div className="text-gray-500">No data available.</div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg shadow">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 font-medium text-gray-500 uppercase tracking-wider">Article ID</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500 uppercase tracking-wider">Views</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {topViewed.map((row) => (
                  <tr key={row.articleId} className="hover:bg-gray-50">
                    <td className="py-3 px-4 font-mono text-sm">{row.articleId}</td>
                    <td className="py-3 px-4">{row.title}</td>
                    <td className="py-3 px-4">{row.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Recent Articles</h3>
          </div>
          <div className="p-4">
            {stats.recentArticles.length === 0 ? (
              <p className="text-gray-500">No articles found</p>
            ) : (
              <ul className="divide-y divide-gray-200">
                {stats.recentArticles.map((article) => (
                  <li key={article._id} className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{article.title}</p>
                        <p className="text-sm text-gray-500">By {article.author} on {formatDate(article.createdAt)}</p>
                      </div>
                      <div>
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${article.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                          {article.approved ? 'Approved' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Recent Feedback</h3>
          </div>
          <div className="p-4">
            {stats.recentFeedback.length === 0 ? (
              <p className="text-gray-500">No feedback found</p>
            ) : (
              <ul className="divide-y divide-gray-200">
                {stats.recentFeedback.map((feedback) => (
                  <li key={feedback._id} className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{feedback.name}</p>
                        <p className="text-sm text-gray-500">{feedback.feedbackType} - {formatDate(feedback.createdAt)}</p>
                      </div>
                      <div>
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          feedback.status === 'new' ? 'bg-blue-100 text-blue-800' :
                          feedback.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {feedback.status === 'new' ? 'New' : 
                           feedback.status === 'in_progress' ? 'In Progress' : 'Resolved'}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

export default Dashboard; 